import MiniSearch from 'minisearch';

// ---- Search document shape (matching generator output) ----

/**
 * Flat search document serialized from a ComponentMapEntry.
 * Array/object fields are space-joined strings for MiniSearch tokenization.
 */
export interface SearchDoc {
  id: string;
  kind: string;
  selector: string;
  symbolName: string;
  sourcePath: string;
  projectName: string;
  searchTokens: string;
  semanticTags: string;
  exposedCssVariables: string;
  consumedCssVariables: string;
  classes: string;
  interactive: string;
  nestedComponents: string;
  usedBy: string;
  reachableByApps: string;
  configMapping: string;
  templatePath: string;
  stylePaths: string;
  relatedPaths: string;
  pipeName: string;
  ownershipScope: string;
  ownershipApp: string;
  ownershipStyleOwner: string;
}

/**
 * Result returned by MiniSearch search(), including BM25 score and matched terms.
 */
export interface ScoredSearchResult extends SearchDoc {
  score: number;
  terms: string[];
}

// ---- Reconstructed ComponentMapEntry shape (subset needed by reasoning engine) ----

interface ReconstructedEntry {
  kind: string;
  selector: string;
  symbolName: string;
  sourcePath: string;
  templatePath?: string;
  searchTokens: string[];
  relatedPaths: string[];
  exposedCssVariables: string[];
  consumedCssVariables: string[];
  semanticTags: string[];
  selectors: {
    nestedComponents: string[];
    interactive: string[];
    classes: string[];
    host: string;
    ids: string[];
  };
  usedBy: string[];
  configMapping?: string;
  reachableByApps: string[];
  stylePaths: string[];
  projectName: string;
  pipeName?: string;
  score?: number;
}

// ---- Index configuration ----

const MINISEARCH_FIELDS = [
  'selector', 'symbolName', 'searchTokens', 'semanticTags',
  'exposedCssVariables', 'classes', 'projectName', 'sourcePath',
  'nestedComponents', 'interactive'
];

const MINISEARCH_STORE_FIELDS = [
  'id', 'kind', 'selector', 'symbolName', 'sourcePath', 'projectName',
  'exposedCssVariables', 'consumedCssVariables', 'classes', 'interactive',
  'nestedComponents', 'usedBy', 'reachableByApps', 'configMapping',
  'templatePath', 'stylePaths', 'relatedPaths', 'searchTokens', 'semanticTags',
  'ownershipScope', 'ownershipApp', 'ownershipStyleOwner', 'pipeName'
];

const MINISEARCH_BOOST: Record<string, number> = {
  selector: 4,
  symbolName: 3,
  semanticTags: 3,
  searchTokens: 2,
  exposedCssVariables: 2,
  classes: 2
};

// ---- Public API ----

/**
 * Creates a MiniSearch instance configured for component search with
 * field boosting, fuzzy matching (0.2), and prefix search enabled.
 */
export function createSearchIndex(): MiniSearch<SearchDoc> {
  return new MiniSearch<SearchDoc>({
    fields: MINISEARCH_FIELDS,
    storeFields: MINISEARCH_STORE_FIELDS,
    searchOptions: {
      boost: MINISEARCH_BOOST,
      prefix: true,
      fuzzy: 0.2
    },
    idField: 'id'
  });
}

/**
 * Restores a pre-built MiniSearch index from the serialized JSON blob
 * produced by the component map generator. Uses MiniSearch.loadJSON
 * which includes the full inverted index, IDF tables, and stored documents.
 * Works in both browser and Node.js.
 */
export function loadSearchIndex(json: object): MiniSearch<SearchDoc> {
  return MiniSearch.loadJSON<SearchDoc>(JSON.stringify(json), {
    fields: MINISEARCH_FIELDS,
    storeFields: MINISEARCH_STORE_FIELDS,
    searchOptions: {
      boost: MINISEARCH_BOOST,
      prefix: true,
      fuzzy: 0.2
    },
    idField: 'id'
  }) as unknown as MiniSearch<SearchDoc>;
}

/**
 * Converts a MiniSearch ScoredSearchResult back into a ComponentMapEntry shape
 * that the reasoning engine (scoring.ts, reasoning-engine.ts) expects.
 * Space-joined fields are split back into arrays.
 */
export function searchResultToEntry(result: ScoredSearchResult): ReconstructedEntry {
  const split = (value: string | undefined): string[] =>
    value ? value.split(' ').filter(Boolean) : [];

  return {
    kind: result.kind,
    selector: result.selector,
    symbolName: result.symbolName,
    sourcePath: result.sourcePath,
    templatePath: result.templatePath || undefined,
    searchTokens: split(result.searchTokens),
    relatedPaths: split(result.relatedPaths),
    exposedCssVariables: split(result.exposedCssVariables),
    consumedCssVariables: split(result.consumedCssVariables),
    semanticTags: split(result.semanticTags),
    selectors: {
      nestedComponents: split(result.nestedComponents),
      interactive: split(result.interactive),
      classes: split(result.classes),
      host: result.selector,
      ids: []
    },
    usedBy: split(result.usedBy),
    configMapping: result.configMapping || undefined,
    reachableByApps: split(result.reachableByApps),
    stylePaths: split(result.stylePaths),
    projectName: result.projectName,
    pipeName: result.pipeName || undefined,
    score: result.score
  };
}

/**
 * Rebuilds the full entries array from a MiniSearch instance that was loaded
 * from a pre-built index. Iterates all stored documents and converts them
 * back to ComponentMapEntry shapes. Called once after loading the search index.
 *
 * This replaces the old approach of loading a separate 14MB component-map.json
 * — all entry data is already embedded in the MiniSearch stored fields (~2.1MB).
 */
export function rebuildEntriesFromIndex(miniSearch: MiniSearch<SearchDoc>): ReconstructedEntry[] {
  // MiniSearch stores loaded documents in _documentIds (Map<string, unknown>)
  const ids: Map<string, unknown> = (miniSearch as never)['_documentIds'];
  const entries: ReconstructedEntry[] = [];

  for (const id of ids.keys()) {
    const stored = miniSearch.getStoredFields(id);
    if (stored) {
      entries.push(searchResultToEntry(stored as unknown as ScoredSearchResult));
    }
  }

  return entries;
}
